<template>
    <div class="bg-white rounded shadow min-h-screen">
        <Row type="none" class="bg-white rounded shadow">
            <div class="container grid grid-cols-12 gap-3 pt-4">
                <div class="col-span-12 lg:col-span-2">
                    <div class="font-bold">{{ $t('statistics.categories.soldProducts') }}</div>
                </div>
                <div class="col-span-12 sm:col-span-3 lg:col-start-5 lg:col-span-2">
                    <b-form-select
                        v-model="currentCurrencyShortName"
                        class="h-11 border-gray-300 focus:ring-0"
                    >
                        <b-form-select-option value="UZS">UZS</b-form-select-option>
                        <b-form-select-option value="USD">USD</b-form-select-option>
                        <b-form-select-option value="RUB">RUB</b-form-select-option>
                    </b-form-select>
                </div>
                <div class="col-span-12 sm:col-span-3 lg:col-span-2">
                    <b-form-select
                        v-model="statPeriod"
                        @change="defineStatPeriod"
                        class="h-11 border-gray-300 focus:ring-0"
                    >
                        <b-form-select-option :value="0">{{ $t('statistics.type.statByDay') }}</b-form-select-option>
                        <b-form-select-option :value="1">{{ $t('statistics.type.statByMonth') }}</b-form-select-option>
                        <b-form-select-option :value="2">{{ $t('statistics.type.statByYear') }}</b-form-select-option>
                    </b-form-select>
                </div>
                <div class="col-span-12 sm:col-span-6 lg:col-span-4">
                    <div v-if="isYearly">
                        <b-form-select
                            class="h-11 border-gray-300 focus:ring-0"
                            v-model="selectedYear"
                            :options="optionsYear"
                            @change="yearlyStat">
                        </b-form-select>
                    </div>

                    <div v-if="isMonthly" class="flex gap-3">
                        <b-form-select
                            class="h-11 border-gray-300 focus:ring-0"
                            v-model="selectedYear"
                            @change="monthlyStat"
                            :options="optionsYear">
                        </b-form-select>
                        <b-form-select
                            class="h-11 border-gray-300 focus:ring-0"
                            v-model="selectedMonth"
                            :options="optionsMonth"
                            @change="monthlyStat">
                        </b-form-select>
                    </div>

                    <div v-if="isDaily">
                        <b-form-datepicker
                            :locale="$i18n.locale === 'uz' ? 'ru' : $i18n.locale"
                            style="height: 44px !important;"
                            class="border-gray-300 focus:ring-0"
                            id="example-datepicker"
                            v-model="currentDate"
                            :hide-header="hideHeader"
                            @input="dailyStat"
                        ></b-form-datepicker>
                    </div>
                </div>
            </div>

            <div class="grid grid-cols-12">
                <div class="col-span-12 pr-3">
                    <AreaChart :series="series" :options="chartOptionsArea" :height=350 />
                </div>
            </div>
        </Row>

        <TodaySaleStat :period="period" class="my-3" :currency-short-name="currentCurrencyShortName"/>

        <Row type="none">
            <div class="grid grid-cols-2 gap-3">
                <!--   pay type chart  -->
                <div class="col-span-2 md:col-span-1 bg-white rounded shadow min-h-72">
                    <div class="grid grid-cols-5">
                        <div class="col-span-5 sm:col-span-2 container">
                            <div class="mt-3 font-bold mb-4">
                                {{ $t('statistics.payTypes') }}
                            </div>

                            <div class="flex mb-3 whitespace-nowrap">
                                <div class="h-5 w-5 bg-main rounded-full flex justify-center items-center">
                                    <div class="h-3 w-3 bg-white rounded-full"></div>
                                </div>
                                <div class="text-gray-400 px-2">{{ $t('statistics.categories.cash') }}</div>
                                <div class="text-gray-500 font-bold"> - {{ this.getCompanyPayTypeStatistics.cashPercent }}%</div>
                            </div>
                            <div class="flex mb-3 whitespace-nowrap" >
                                <div class="h-5 w-5 bg-orangeColor rounded-full flex justify-center items-center">
                                    <div class="h-3 w-3 bg-white rounded-full"></div>
                                </div>
                                <div class="text-gray-400 px-2">{{ $t('statistics.categories.debt') }}</div>
                                <div class="text-gray-500 font-bold"> - {{ this.getCompanyPayTypeStatistics.debtPercent }}%</div>
                            </div>
                            <div class="flex mb-3 whitespace-nowrap" >
                                <div class="h-5 w-5 bg-blue-400 rounded-full flex justify-center items-center">
                                    <div class="h-3 w-3 bg-white rounded-full"></div>
                                </div>
                                <div class="text-gray-400 px-2">{{ $t('statistics.categories.bankCard') }}</div>
                                <div class="text-gray-500 font-bold"> - {{ this.getCompanyPayTypeStatistics.bankCardPercent }}%</div>
                            </div>
                            <div class="flex mb-3 whitespace-nowrap" >
                                <div class="h-5 w-5 bg-green-400 rounded-full flex justify-center items-center">
                                    <div class="h-3 w-3 bg-white rounded-full"></div>
                                </div>
                                <div class="text-gray-400 px-2">{{ $t('statistics.categories.moneyTransfer') }}</div>
                                <div class="text-gray-500 font-bold"> - {{ this.getCompanyPayTypeStatistics.moneyTransferPercent }}%</div>
                            </div>
                        </div>

                        <div class="col-span-5 sm:col-span-3">
                            <apexchart type="donut" height="250" class="mt-3.5" :options="chartOptionsPayType" :series="chartSeriesPayType"></apexchart>
                        </div>
                    </div>
                </div>

                <!--   top product chart  -->
                <div class="col-span-2 md:col-span-1 bg-white rounded shadow min-h-72">
                    <div class="grid grid-cols-2 gap-3">
                        <div class="col-span-2 sm:col-span-1 container mt-4">
                            <router-link :to="$i18nHelper.getUrl('statistics/product')"
                                         class="underline font-bold py-2">
                                {{ $t('statistics.product') }}
                            </router-link>
                        </div>
                        <div class="col-span-2 sm:col-span-1 container mt-3">
                            <b-form-select
                                @change="topProductPoint"
                                v-model="topProduct"
                                class="h-11 border-gray-200 focus:ring-0">
                                <b-form-select-option value="amount">{{ $t('statistics.byAmount') }}</b-form-select-option>
                                <b-form-select-option value="percent">{{ $t('statistics.byPercent') }}</b-form-select-option>
                            </b-form-select>
                        </div>
                    </div>
                    <div v-if="isAmount" class="grid grid-cols-2 gap-3 mt-3">
                        <div v-for="(topProduct, index) of currentPeriodTopProducts" :key="index" class="col-span-2 sm:col-span-1 container pb-3">
                            <div class="flex justify-content-between">
                                <div class="text-xs text-gray-500">{{ topProduct.product.name }}</div>
                                <div class="text-xs text-gray-800">{{ topProduct.amount }}</div>
                            </div>
                            <b-progress class="progress" :value="topProduct.amount" :max="topProduct.totalAmount"></b-progress>
                        </div>
                    </div>
                    <div v-else class="grid grid-cols-2 gap-3 mt-3">
                        <div v-for="(topProduct, index) of currentPeriodTopProducts" :key="index" class="col-span-2 sm:col-span-1 container pb-3">
                            <div class="flex justify-content-between">
                                <div class="text-xs text-gray-500">{{ topProduct.product.name }}</div>
                                <div class="text-xs text-gray-800">{{ parseInt(topProduct.percentAmount) }} %</div>
                            </div>
                            <b-progress class="progress" :value="topProduct.percentAmount" :max="100"></b-progress>
                        </div>
                    </div>
                </div>
            </div>
        </Row>

<!--        <Row type="none" class="bg-white rounded shadow">-->
<!--            -->
<!--            &lt;!&ndash;  thirdly chart   &ndash;&gt;-->
<!--            <div class="row px-2 mt-4">-->
<!--                <div class="col-12 col-md-4">-->
<!--                    <div class="card">-->
<!--                        <div class="col-12 font-weight-bold pl-4 pt-4 mb-n3">{{ $t('statistics.type.weekly') }}</div>-->
<!--                        <div class="col-12">-->
<!--                            <BarChart :series="chartSeriesBarWeek" :options="chartOptionsBarWeek" :height="250"/>-->
<!--                        </div>-->
<!--                    </div>-->
<!--                </div>-->
<!--                <div class="col-12 col-md-8">-->
<!--                    <div class="card">-->
<!--                        <div v-if="isDaily" class="col-12 font-weight-bold pl-4 pt-4 mb-n3">{{ $t('statistics.type.hourly') }}</div>-->
<!--                        <div v-if="isMonthly" class="col-12 font-weight-bold pl-4 pt-4 mb-n3">{{ $t('statistics.type.daily') }}</div>-->
<!--                        <div v-if="isYearly" class="col-12 font-weight-bold pl-4 pt-4 mb-n3">{{ $t('statistics.type.monthly') }}</div>-->
<!--                        <div class="col-12">-->
<!--                            <BarChart :series="chartSeriesBarDay" :options="chartOptionsBarDay" :height="250"/>-->
<!--                        </div>-->
<!--                    </div>-->

<!--                </div>-->
<!--            </div>-->

<!--        </Row>-->

    </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import Vue from "vue";
import VueApexCharts from "vue-apexcharts";
import AreaChart from "@/pages/Statistics/Charts/AreaChart.vue";
import dateFormattingMixin from "@/mixin/dateFormattingMixin";
import currencyFormatMixin from "@/mixin/currencyFormatMixin";
import TodaySaleStat from "@/components/TodaySaleStat.vue";
import Row from "@/components/html/Row.vue";

Vue.use(VueApexCharts)
Vue.component('apexchart', VueApexCharts)
export default {
    name: "SaleStat",
    components: {Row, TodaySaleStat, AreaChart},
    mixins: [dateFormattingMixin, currencyFormatMixin],
    data() {
        return {
            url: process.env.VUE_APP_API_URL,
            breadcrumbItems: [
                {name: 'home', url: ''},
                {name: 'statistics.sale'},
            ],
            max: 100,
            period: '',
            statPeriod: 0,
            weekday: null,
            isDaily: true,
            isMonthly: false,
            isYearly: false,
            isAmount: true,
            isPercent: false,
            selectedMonth: null,
            selectedYear: null,
            optionsMonth: [
                {value: {start: '01-01', end: '01-31'}, text: this.$t('statistics.month.Jan')},
                {value: {start: '02-01', end: '02-28'}, text: this.$t('statistics.month.Feb')},
                {value: {start: '03-01', end: '03-31'}, text: this.$t('statistics.month.Mar')},
                {value: {start: '04-01', end: '04-30'}, text: this.$t('statistics.month.Apr')},
                {value: {start: '05-01', end: '05-31'}, text: this.$t('statistics.month.May')},
                {value: {start: '06-01', end: '06-30'}, text: this.$t('statistics.month.Jun')},
                {value: {start: '07-01', end: '07-31'}, text: this.$t('statistics.month.Jul')},
                {value: {start: '08-01', end: '08-31'}, text: this.$t('statistics.month.Aug')},
                {value: {start: '09-01', end: '09-30'}, text: this.$t('statistics.month.Sep')},
                {value: {start: '10-01', end: '10-31'}, text: this.$t('statistics.month.Oct')},
                {value: {start: '11-01', end: '11-30'}, text: this.$t('statistics.month.Nov')},
                {value: {start: '12-01', end: '12-31'}, text: this.$t('statistics.month.Dec')},
            ],
            hours: [0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23],
            days: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30, 31],
            month: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12],
            optionsYear: [],
            currentDate: null,
            currentMonth: null,
            hideHeader: false,
            modelFormCurrency: 'UZS',
            topProduct: 'amount',
            currentPeriodTopProducts: [],
            search: {
                dateFrom: '',
                dateTo: '',
                isPayed: null,
                employeeId: null,
                isDaily: false,
                isMonthly: false,
            },
            chartOptionsArea: {
                chart: {
                    id: '1',
                    type: 'area',
                    toolbar: {
                        show: false,
                    }
                },
                grid: {
                    show: true,
                    borderColor: '#F6F7FB',
                },
                colors: ['#8300C5'],
                dataLabels: {
                    enabled: false,
                },
                stroke: {
                    show: true,
                    curve: 'smooth',
                    lineCap: 'round',
                    colors: undefined,
                    width: 1,
                    dashArray: 0,
                },
                xaxis: {
                    min: 1,
                    title: {
                        text: this.$t('statistics.xaxis.hour') + '/' + this.$t('statistics.xaxis.day'),
                        offsetX: 0,
                        offsetY: -10,
                        style: {
                            color: '#8300C5',
                            fontSize: '12px',
                            fontFamily: 'Helvetica, Arial, sans-serif',
                            fontWeight: 600,
                            cssClass: 'apexcharts-xaxis-title',
                        },
                    },
                },
                yaxis: {
                    showAlways: false,
                    decimalsInFloat: 0,
                    labels: {
                        show: true,
                        formatter: function abbrNum(number, decPlaces) {
                            decPlaces = Math.pow(10, decPlaces);

                            let abbrev = ["k", "m", "b", "t"];

                            for (let i = abbrev.length - 1; i >= 0; i--) {
                                let size = Math.pow(10, (i + 1) * 3);

                                if (size <= number) {
                                    number = Math.round(number * decPlaces / size) / decPlaces;

                                    if ((number === 1000) && (i < abbrev.length - 1)) {
                                        number = 1;
                                        i++;
                                    }

                                    number += abbrev[i];

                                    break;
                                }
                            }

                            return number;
                        }
                    },
                    tickAmount: 6,
                },
                responsive: [
                    {
                        breakpoint: 500,
                        options: {
                            chart: {
                                height: 300,
                                width: '100%',
                                type: 'area',
                                toolbar: {
                                    show: false,
                                }
                            },
                            xaxis: {
                                labels: {
                                    show: true,
                                    style: {
                                        fontSize: '8px',
                                        fontFamily: 'Helvetica, Arial, sans-serif',
                                        fontWeight: 400,
                                        cssClass: 'apexcharts-xaxis-label',
                                    },
                                },
                                title: {
                                    show: false,
                                    text: '',
                                },
                            },
                            yaxis: {
                                title: {
                                    show: false,
                                    text: '',
                                },
                                tickAmount: 10,
                                labels: {
                                    style: {
                                        fontSize: '8px',
                                        fontFamily: 'Helvetica, Arial, sans-serif',
                                        fontWeight: 400,
                                        cssClass: 'apexcharts-yaxis-label',
                                    },
                                    formatter: function abbrNum(number, decPlaces) {
                                        decPlaces = Math.pow(10, decPlaces);

                                        let abbrev = ["k", "m", "b", "t"];

                                        for (let i = abbrev.length - 1; i >= 0; i--) {
                                            let size = Math.pow(10, (i + 1) * 3);

                                            if (size <= number) {
                                                number = Math.round(number * decPlaces / size) / decPlaces;

                                                if ((number === 1000) && (i < abbrev.length - 1)) {
                                                    number = 1;
                                                    i++;
                                                }

                                                number += abbrev[i];

                                                break;
                                            }
                                        }

                                        return number;
                                    }
                                }
                            },
                        },
                    },
                ]
            },
            series: [{
                name: this.$t('statistics.categories.soldProducts'),
                data: []
            }],

            chartOptionsDonutPercent: {
                chart: {
                    type: "donut"
                },
                plotOptions: {
                    pie: {
                        donut: {
                            labels: {
                                show: true,
                                value: {
                                    show: true,
                                    fontSize: '14px',
                                    fontFamily: 'Helvetica, Arial, sans-serif',
                                    color: undefined,
                                    offsetY: -10,
                                    formatter: function (val) {
                                        return val
                                    }
                                },
                                total: {
                                    show: true,
                                    label: '',
                                    color: '#373d3f',
                                    formatter: function (w) {
                                        return w.globals.seriesTotals.reduce((a, b) => {
                                            return Math.round(a * 100 / b) + '%'
                                        })
                                    }
                                }
                            }
                        }
                    }
                },
                colors: ["#8300C5", "#c5c5c5"],
                dataLabels: {
                    enabled: false
                },
                legend: {
                    show: false
                }
            },
            chartOptionsDonutAmount: {
                chart: {
                    type: "donut",
                },
                labels: [this.$t('amount'), this.$t('total')],
                plotOptions: {
                    pie: {
                        donut: {
                            labels: {
                                show: true,
                                value: {
                                    show: true,
                                    fontSize: '14px',
                                    fontFamily: 'Helvetica, Arial, sans-serif',
                                    color: undefined,
                                    offsetY: -10,
                                    formatter: function (val) {
                                        return val
                                    }
                                },
                                total: {
                                    show: true,
                                    label: '',
                                    color: '#373d3f',
                                    formatter: function (w) {
                                        return w.globals.seriesTotals.reduce((a) => {
                                            return Math.round(a)
                                        })
                                    }
                                }
                            }
                        }
                    }
                },
                colors: ["#8300C5", "#c5c5c5"],
                dataLabels: {
                    enabled: false
                },
                legend: {
                    show: false
                },
                tooltip: {
                    enabled: false
                }
            },

            chartOptionsBarWeek: {
                chart: {
                    id: '3',
                    toolbar: {
                        show: false,
                    },
                    type: 'bar'
                },
                colors: ['#8300C5'],
                dataLabels: {
                    enabled: false
                },
                legend: {
                    show: false
                },
                xaxis: {
                    type: 'category',
                    categories: [],
                },
            },
            chartSeriesBarWeek: [{
                name: this.$t('statistics.categories.soldProducts'),
                data: [21, 22, 10, 28, 16, 21, 13]
            }],

            chartOptionsBarDay: {
                chart: {
                    id: '4',
                    toolbar: {
                        show: false,
                    },
                    type: 'bar'
                },
                colors: ['#8300C5'],
                plotOptions: {
                    bar: {
                        columnWidth: '75%',
                        distributed: true,
                    }
                },
                dataLabels: {
                    enabled: false
                },
                legend: {
                    show: false
                },
                xaxis: {
                    type: 'category',
                    categories: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 26, 27, 28, 29, 30],
                },
            },
            chartSeriesBarDay: [{
                name: this.$t('statistics.categories.soldProducts'),
                data: [21, 22, 10, 28, 16, 21, 13, 21, 22, 10, 28, 16, 21, 13, 21, 22, 10, 28, 16, 21, 13, 21, 22, 10, 28, 16, 21, 13]
            }],

            chartOptionsPayType: {
                labels: [this.$t('statistics.categories.cash'), this.$t('statistics.categories.debt'), this.$t('statistics.categories.bankCard'), this.$t('statistics.categories.moneyTransfer')],
                dataLabels: {
                    enabled: false,
                },
                legend: {
                    show: false,
                },
                fill: {
                    type: 'gradient',
                },
                plotOptions: {
                    pie: {
                        startAngle: -90,
                        endAngle: 270,
                        donut: {
                            size: '90%'
                        },
                        dataLabels: {
                            offset: -88
                        }
                    }
                },
                colors: ['#6f42c1', '#FF8A5A', '#4BBBF9',  "#6ff15a"],
            },
            chartSeriesPayType: []
        }
    },
    computed: {
        ...mapGetters([
            'getCompanyPayTypeStatistics',
            'getUserCompanyCreatedAt',
            'getTopProducts',
            'getSaleDaily',
            'getSaleWeekly',
            'getSaleMonthly',
            'getSaleYearly'
        ])
    },
    methods: {
        ...mapActions([
            'fetchUser',
            'fetchCompanyPayTypeStatistics',
            'fetchTopProducts',
            'fetchTopProductsLastPeriod',
            'fetchSaleDailyStat',
            'fetchSaleWeeklyStat',
            'fetchSaleMonthlyStat',
            'fetchSaleYearlyStat',
            'setBreadcrumbItems'
        ]),
        defineStatPeriod() {
            if (this.statPeriod === 0) {
                this.statByDays()
            } else if (this.statPeriod === 1) {
                this.statByMonth()
            } else {
                this.statByYear()
            }
        },
        statByDays() {
            this.isDaily = true
            this.isMonthly = false
            this.isYearly = false
            this.$store.state.saleProduct.topProducts.isNeedFetch = true
            this.$store.state.company.companyPayTypeStatistics.isNeedFetch = true
            this.$store.state.saleDaily.saleDaily.isNeedFetch = true
            this.currentDate = this.getTodayDate()

            this.periodDefiner(new Date())
            this.fetchTopProductsLastPeriod()
                .then(() => {
                    this.currentPeriodTopProducts = this.getTopProducts.daily.slice(0, 8)
                })
            this.fetchCompanyPayTypeStatistics({
                dateFrom: this.getTodayDate() + ' 00:00:00',
                dateTo: this.getTodayDate() + ' 23:59:59'
            })
                .then(() => {
                    this.chartSeriesBarWeek[0].data = this.getSaleWeekly

                    this.chartSeriesPayType = [this.getCompanyPayTypeStatistics.cashPercent, this.getCompanyPayTypeStatistics.debtPercent, this.getCompanyPayTypeStatistics.bankCardPercent, this.getCompanyPayTypeStatistics.moneyTransferPercent]

                    this.updateChartOptions('3', this.weekDaysCreator(), 'day')
                })
            this.fetchSaleDailyStat()
                .then(() => {
                    this.chartSeriesBarDay[0].data = this.getSaleDaily
                    this.series[0].data = this.getSaleDaily

                    this.updateChartOptions('1', this.hours, 'hour')
                    this.updateChartOptions('4', this.hours, 'hour')
                })
        },
        statByMonth() {
            this.isDaily = false
            this.isMonthly = true
            this.isYearly = false
            this.series[0].data = this.getSaleMonthly
            this.search.dateFrom = this.selectedYear + '-' + this.selectedMonth.start
            this.search.dateTo = this.selectedYear + '-' + this.selectedMonth.end
            this.$store.state.company.companyPayTypeStatistics.isNeedFetch = true
            this.$store.state.saleProduct.topProducts.isNeedFetch = true

            this.periodDefiner(this.search.dateFrom, this.search.dateTo)
            this.fetchTopProductsLastPeriod()
                .then(() => {
                    this.currentPeriodTopProducts = this.getTopProducts.monthly.slice(0, 8)
                })
            this.fetchCompanyPayTypeStatistics(this.search)
                .then(() => {
                    this.chartSeriesBarDay[0].data = this.getSaleMonthly
                    this.updateChartOptions('4', this.days, 'day')
                    this.chartSeriesPayType = [this.getCompanyPayTypeStatistics.cashPercent, this.getCompanyPayTypeStatistics.debtPercent, this.getCompanyPayTypeStatistics.bankCardPercent, this.getCompanyPayTypeStatistics.moneyTransferPercent]

                })

            this.updateChartOptions('1', this.days, 'day')
        },
        statByYear() {
            this.isDaily = false
            this.isMonthly = false
            this.isYearly = true
            this.series[0].data = this.getSaleYearly
            this.search.dateFrom = this.selectedYear + '-' + 1 + '-' + 1
            this.search.dateTo = this.selectedYear + '-' + 12 + '-' + 31
            this.$store.state.saleProduct.topProducts.isNeedFetch = true
            this.$store.state.company.companyPayTypeStatistics.isNeedFetch = true

            this.periodDefiner(this.selectedYear, null, true)
            this.fetchTopProductsLastPeriod()
                .then(() => {
                    this.currentPeriodTopProducts = this.getTopProducts.yearly.slice(0, 8)
                })
            this.fetchCompanyPayTypeStatistics(this.search)
                .then(() => {
                    this.chartSeriesBarDay[0].data = this.getSaleYearly
                    this.updateChartOptions('4', this.month, 'year')
                    this.chartSeriesPayType = [this.getCompanyPayTypeStatistics.cashPercent, this.getCompanyPayTypeStatistics.debtPercent, this.getCompanyPayTypeStatistics.bankCardPercent, this.getCompanyPayTypeStatistics.moneyTransferPercent]

                })

            this.updateChartOptions('1', this.month, 'year')
        },
        dailyStat() {
            let date = {dateFrom: this.currentDate + 'T00:00:00', dateTo: this.currentDate + 'T23:59:59', isDaily: true}
            this.$store.state.company.companyPayTypeStatistics.isNeedFetch = true
            this.$store.state.saleProduct.topProducts.isNeedFetch = true
            this.$store.state.saleDaily.saleDaily.isNeedFetch = true

            this.periodDefiner(date.dateFrom)
            this.makeRequestTopProducts(date)
            this.fetchCompanyPayTypeStatistics(date)
            this
                .fetchSaleDailyStat(date)
                .then(() => {
                    this.series[0].data = this.getSaleDaily
                    this.chartSeriesBarDay[0].data = this.getSaleDaily
                    this.updateChartOptions('1', this.hours, 'hour')
                    this.updateChartOptions('4', this.hours, 'hour')
                })
        },
        monthlyStat() {
            if (this.selectedYear === null && this.selectedMonth === null) {
                return
            }

            let date = {
                dateFrom: this.selectedYear + '-' + this.selectedMonth.start + 'T00:00:00',
                dateTo: this.selectedYear + '-' + this.selectedMonth.end + 'T23:59:59',
                isMonthly: true
            }

            this.$store.state.company.companyPayTypeStatistics.isNeedFetch = true
            this.$store.state.saleMonthly.saleMonthly.isNeedFetch = true
            this.$store.state.saleProduct.topProducts.isNeedFetch = true

            this.periodDefiner(date.dateFrom, date.dateTo)
            this.makeRequestTopProducts(date)
            this.fetchCompanyPayTypeStatistics(date)
            this
                .fetchSaleMonthlyStat({date: date.dateFrom})
                .then(() => {
                    this.series[0].data = this.getSaleMonthly
                    this.chartSeriesBarDay[0].data = this.getSaleMonthly
                    this.updateChartOptions('4', this.days, 'day')
                    this.updateChartOptions('1', this.days, 'day')
                })
        },
        yearlyStat() {
            let date = {dateFrom: this.selectedYear + '-01-01T00:00:00', dateTo: this.selectedYear + '-12-31T23:59:59'}
            this.$store.state.company.companyPayTypeStatistics.isNeedFetch = true
            this.$store.state.saleYearly.saleYearly.isNeedFetch = true
            this.$store.state.saleProduct.topProducts.isNeedFetch = true

            this.periodDefiner(this.selectedYear, null, true)
            this.makeRequestTopProducts(date)
            this.fetchCompanyPayTypeStatistics(date)
            this
                .fetchSaleYearlyStat({year: parseInt(this.selectedYear)})
                .then(() => {
                    this.series[0].data = this.getSaleYearly
                    this.chartSeriesBarDay[0].data = this.getSaleYearly
                    this.updateChartOptions('4', this.month, 'month')
                    this.updateChartOptions('1', this.month, 'month')
                })
        },
        periodDefiner(from, to = null, isYear = false) {
            if (isYear) {
                this.period = from.toString()
                return
            }

            if (to === null) {
                this.period = this.dateFormat(from)
            } else {
                let start = this.dateFormat(from)
                let end = this.dateFormat(to)
                this.period = start + ' - ' + end
            }
        },
        updateChartOptions(chartId, categories, text) {
            this.$apexcharts.exec(chartId, 'updateOptions', {
                xaxis: {
                    type: 'category',
                    categories: categories,
                    title: {
                        text: this.$t(`statistics.xaxis.${text}`),
                    }
                }
            })
        },
        topProductPoint() {
            console.log('ffofo')
            switch (this.topProduct) {
                case "amount":
                    this.isAmount = true
                    this.isPercent = false
                    break

                case "percent":
                    this.isAmount = false
                    this.isPercent = true
                    break
            }
        },
        async createYear() {
            let created = parseInt(this.getUserCompanyCreatedAt)

            while (created <= new Date().getFullYear()) {
                this.optionsYear.unshift({value: created, text: created})
                created++
            }

            await this.setYear()
        },
        weekDaysCreator() {
            let days = []
            let dayWeek = 6
            let length = this.$t('statistics.weekdays').length
            for (let i = 0; i <= length - 1; i++) {
                let today = new Date()
                today.setDate(today.getDate() - dayWeek)
                days.push(this.$t('statistics.weekdays')[today.getDay()])
                dayWeek--
            }

            return days
        },
        setYear() {
            this.selectedYear = this.optionsYear[0].value
        },
        dateSwitcher() {
            this.selectedMonth = this.optionsMonth[this.currentMonth - 1].value
        },
        makeRequestTopProducts(date) {
            this.fetchTopProducts(date)
                .then(() => {
                    if (this.getTopProducts.length === 0) {
                        this.currentPeriodTopProducts = []

                        do {
                            this.currentPeriodTopProducts.push({
                                amount: 0,
                                totalAmount: 100,
                                percentAmount: 0,
                                product: {name: ''}
                            })
                        }
                        while (this.currentPeriodTopProducts.length < 4)
                    } else {
                        this.currentPeriodTopProducts = this.getTopProducts.slice(0, 8)
                    }
                })
        },
        getTodayDate() {
            let yearNow = new Date().getFullYear()
            let monthNow = new Date().getMonth() + 1
            let dateNow = new Date().getDate()

            return yearNow + '-' + this.addZero(monthNow) + '-' + this.addZero(dateNow)
        },
        addZero(val) {
            return val < 10 ? '0' + val : val;
        },
    },
    created() {
        this.setBreadcrumbItems(this.breadcrumbItems)
    },
    mounted() {
        this.$root.showOverlay = true
        this
            .fetchUser()
            .then(() => {
                this.currentMonth = new Date().getUTCMonth() + 1
                this.currentDate = this.getTodayDate()
                this.periodDefiner(this.getTodayDate())
                this.createYear()
                this.dateSwitcher()

                this.fetchSaleWeeklyStat()
                this.fetchSaleDailyStat()
                    .then(() => {
                        this.statByDays()
                        this.$root.showOverlay = false
                        this.$i18nHelper.flash(this.$t('statistics.info.gap'), 'info')
                    })
                    .catch((error) => {
                        console.log(error)
                        this.$root.showOverlay = false
                    })
                this.fetchSaleMonthlyStat()
                    .catch((error) => {
                        console.log(error)
                    })
                this.fetchSaleYearlyStat()
                    .catch((error) => {
                        console.log(error)
                    })
            })
            .catch((error) => {
                console.log(error)
                this.$root.showOverlay = false
            })
    }
}
</script>

<style scoped>

.progress {
    width: 100% !important;
    height: 8px;
}

</style>
